/**
 * CreateTemplateDialog — create a custom form template.
 *
 * Allows users to bundle existing form components into a reusable template.
 * System-built templates have is_system=True and cannot be modified.
 */

import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, processesApi } from '../../../../api';
import type { BaseFormComponent } from '../types';
import { getTemplateDomainLabel } from '../helpers';
import { getFormComponentDisplay } from '../../processDisplay';
import { translateApiError } from '../../../../i18n/translateApiError';

interface CreateTemplateDialogProps {
  open: boolean;
  components: BaseFormComponent[];
  /** EDIT MODE when present. `null` / absent means create.
   *
   * Templates could be created and deleted but never MODIFIED: the backend has shipped
   * `PATCH /v1/processes/form-templates/{id}` and `formTemplatesApi.update` since migration 111,
   * with ZERO call sites in `src/` — so changing one field's width meant deleting the template and
   * rebuilding it (owner, 2026-09-01).
   *
   * One dialog for both verbs rather than a second component: the fields, the validation and the
   * item editor are identical, and a copy of ~200 lines is how the two would come to disagree about
   * what a template may contain. */
  template?: FormTemplateForEdit | null;
  onClose: () => void;
  onCreated: () => void;
}

/** The subset of a template this dialog reads. Structural, so the page can pass its mapped row. */
export interface FormTemplateForEdit {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  difficulty?: string | null;
  estimatedTime?: number | null;
  items: Array<{ component_id: string; col_span: number }>;
}

/** One row of the template, mirroring `FormTemplateItem` on the wire. `col_span` is on
 *  the 12-column grid the Designer canvas uses, so 12 is full width and 6 sits beside
 *  its neighbour. */
interface TemplateItem {
  component_id: string;
  col_span: number;
}

const CATEGORIES = ['HR', 'Finance', 'IT', 'Operations', 'General'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];

const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  open, components, template = null, onClose, onCreated,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [difficulty, setDifficulty] = useState('Medium');
  const [estimatedTime, setEstimatedTime] = useState<number | ''>(30);
  /** ORDERED, and each item carries its own width.
   *
   *  Was `Set<string>`, which is why this dialog could only ever be a multi-select: a set
   *  has no order and no room for a per-item property, so "解决在创建组件模版中只能多选组件
   *  的问题" was not a missing control, it was a missing shape. Backed by
   *  `form_templates.items` since migration 111. */
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName(''); setDescription(''); setCategory('General');
    setDifficulty('Medium'); setEstimatedTime(30);
    setItems([]); setError(null);
  };

  /** Fill from the template each time the dialog OPENS, and clear when it opens to create.
   *
   * Keyed on `open` as well as the template, so re-opening after a cancel does not show the edits
   * that were abandoned. `template?.id` rather than the object, because the page rebuilds its rows
   * on every fetch and a new identity for the same template would reset the form mid-edit
   * (session-discipline §11). */
  useEffect(() => {
    if (!open) return;
    if (!template) { reset(); return; }
    setName(template.name ?? '');
    setDescription(template.description ?? '');
    setCategory(template.category || 'General');
    setDifficulty(template.difficulty || 'Medium');
    setEstimatedTime(typeof template.estimatedTime === 'number' ? template.estimatedTime : 30);
    setItems(template.items.map((item) => ({ ...item })));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const selectedIds = useMemo(() => new Set(items.map((item) => item.component_id)), [items]);

  /** Catalog click: append at the END, or remove. Appending rather than inserting at a
   *  remembered position, because the order is now the admin's to arrange below and a
   *  "clever" insertion point would fight them for it. */
  const toggle = (id: string) => {
    setItems((prev) => (
      prev.some((item) => item.component_id === id)
        ? prev.filter((item) => item.component_id !== id)
        : [...prev, { component_id: id, col_span: 12 }]
    ));
  };

  const setSpan = (id: string, colSpan: number) => {
    setItems((prev) => prev.map((item) => (
      item.component_id === id ? { ...item, col_span: colSpan } : item
    )));
  };

  const moveItem = (from: number, to: number) => {
    setItems((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = Array.from(prev);
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('processes:createTemplate.nameRequired'));
      return;
    }
    if (items.length === 0) {
      setError(t('processes:createTemplate.selectAtLeastOne'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        category,
        difficulty,
        items,
        estimated_time: typeof estimatedTime === 'number' ? estimatedTime : null,
      };
      if (template) {
        // `is_popular` is NOT sent on update: it is not on this form, and PATCH replaces what it
        // receives — including it would silently reset a flag an operator may have set elsewhere.
        await processesApi.formTemplatesApi.update(template.id, payload);
      } else {
        await processesApi.formTemplatesApi.create({ ...payload, is_popular: false });
      }
      reset();
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:createTemplate.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Group components by category for easier selection
  const grouped = useMemo(() => {
    const groups = new Map<string, BaseFormComponent[]>();
    for (const c of components) {
      const list = groups.get(c.category) ?? [];
      list.push(c);
      groups.set(c.category, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [components]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t(template ? 'processes:createTemplate.editTitle' : 'processes:createTemplate.title')}</Typography>
        <IconButton aria-label={t('common:actions.close')} onClick={handleClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <TextField
            label={t('processes:createTemplate.templateName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('processes:createTemplate.templateNamePlaceholder')}
            fullWidth required size="small"
          />

          <TextField
            label={t('processes:createTemplate.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('processes:createTemplate.descriptionPlaceholder')}
            fullWidth multiline rows={2} size="small"
          />

          <Stack direction="row" spacing={2}>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>{t('processes:createTemplate.category')}</InputLabel>
              <Select value={category} label={t('processes:createTemplate.category')} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{t(`processes:formComponents.categories.${c.toLowerCase()}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>{t('processes:createTemplate.difficulty')}</InputLabel>
              <Select value={difficulty} label={t('processes:createTemplate.difficulty')} onChange={(e) => setDifficulty(e.target.value)}>
                {DIFFICULTIES.map(d => <MenuItem key={d} value={d}>{t(`processes:formComponents.difficulties.${d.toLowerCase()}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label={t('processes:createTemplate.estTime')}
              type="number"
              value={estimatedTime}
              onChange={(e) => setEstimatedTime(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              // `flex: 1`, not a fixed 140px: two flexing selects beside one fixed field
              // gave the row three unequal columns whose right edge moved with the
              // dialog width. Equal thirds share the row's alignment (svdy-ui §11.4 —
              // fixed-width children in a flex row are what makes an edge ragged).
              sx={{ flex: 1 }}
              size="small"
              inputProps={{ min: 0 }}
            />
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('processes:createTemplate.selectComponents', { count: selectedIds.size })}
            </Typography>
            {components.length === 0 ? (
              <Alert severity="info">{t('processes:createTemplate.noComponents')}</Alert>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {grouped.map(([cat, catComponents]) => (
                  <Box key={cat} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                    <Typography variant="caption" sx={{ px: 2, py: 0.75, display: 'block', bgcolor: 'grey.100', fontWeight: 600 }}>
                      {getTemplateDomainLabel(t, cat)}
                    </Typography>
                    <List dense disablePadding>
                      {catComponents.map(c => (
                        <ListItem key={c.id} disablePadding>
                          <ListItemButton onClick={() => toggle(c.id)} dense>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox edge="start" size="small" checked={selectedIds.has(c.id)} tabIndex={-1} disableRipple />
                            </ListItemIcon>
                            {/* ONE line per row. The description was a `secondary` line,
                                which doubled every row to ~58px: fifteen components came to
                                ~870px inside a 300px scroller, so picking four meant
                                scrolling past most of the list. The description survives as
                                the row's `title` — available on hover, not costing height
                                (svdy-ui §2: density over decoration). */}
                            <ListItemText
                              primary={getFormComponentDisplay(t, c).label}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                noWrap: true,
                                title: getFormComponentDisplay(t, c).description,
                              }}
                            />
                            {c.isSystem && <Chip label={t('processes:formComponents.dialog.system')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* THE ORDERED LIST. The catalog above answers "which components"; this answers
              "in what order, and how wide" — the two questions the old multi-select could
              not tell apart because a Set holds neither answer.

              Reorder is arrow buttons rather than drag-and-drop, deliberately: this list is
              3-8 rows inside a scrolling dialog, where a drag has to auto-scroll its own
              container to be usable, and `react-beautiful-dnd` inside a MUI Dialog needs a
              portal to avoid the transform clipping its placeholder. Two buttons are
              keyboard-reachable, need no portal, and are unambiguous at this size. The
              CANVAS keeps drag, where the surface is large and already wired for it. */}
          {items.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('processes:createTemplate.orderAndWidth')}
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {items.map((item, index) => {
                  const component = components.find((c) => c.id === item.component_id);
                  return (
                    <Box
                      key={item.component_id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
                        borderBottom: index === items.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="caption" color="text.disabled" sx={{ width: 18, flexShrink: 0 }}>
                        {index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, minWidth: 0 }}
                        noWrap
                        title={component ? getFormComponentDisplay(t, component).description : ''}
                      >
                        {component ? getFormComponentDisplay(t, component).label : item.component_id}
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={item.col_span}
                        onChange={(_, value) => { if (value) setSpan(item.component_id, value); }}
                        sx={{
                          flexShrink: 0,
                          '& .MuiToggleButton-root': {
                            py: 0, px: 1, height: 22, fontSize: '0.65rem',
                            textTransform: 'none', borderRadius: 1,
                          },
                        }}
                      >
                        <ToggleButton value={12}>{t('processes:createTemplate.fullWidth')}</ToggleButton>
                        <ToggleButton value={6}>{t('processes:createTemplate.halfWidth')}</ToggleButton>
                      </ToggleButtonGroup>
                      <IconButton
                        size="small"
                        aria-label={t('processes:createTemplate.moveUp')}
                        disabled={index === 0}
                        onClick={() => moveItem(index, index - 1)}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('processes:createTemplate.moveDown')}
                        disabled={index === items.length - 1}
                        onClick={() => moveItem(index, index + 1)}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('common:actions.remove')}
                        onClick={() => toggle(item.component_id)}
                      >
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ py: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || items.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {saving
            ? <CircularProgress size={16} color="inherit" />
            : t(template ? 'processes:createTemplate.saveChanges' : 'processes:createTemplate.createTemplate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateDialog;
